import {
  EntityOperation,
  logEntityOperationFailure,
} from 'serviceWorker/helpers/entityFailureLog';
import type { PendingPublish } from './pendingPublish';
import { Services } from 'shared/domain/messages/message';
import hashSum from 'hash-sum';
import type { AnyChannelContract } from 'shared/channels/contract';
import { ServiceData } from 'serviceWorker/db/service';
import type { DataOwner } from 'serviceWorker/db/dataOwner';
import { isSameAsPrev } from './isSameAsPrev';
import { PullStep, pullStepMarker } from './pullProgress';
import { runPullInBackground } from './runPullInBackground';
import { isInvalidSyncKeyError } from './invalidSyncKeyError';
import { HeadersData, NormalizedEntityResponseData } from './types';
import { handleAbortError } from 'serviceWorker/helpers/abortErrorHandler';
import { resolveAuthenticatedConfig } from '../config';
import { createConfigRetry, logWaitsForConfig } from 'serviceWorker/helpers/createConfigRetry';
import { subscribeSwLifecycleAbort } from 'serviceWorker/helpers/swLifecycleAbort';
import { captureCollectionScope } from 'serviceWorker/helpers/dataScope';
import { CollectionRepairReason } from 'serviceWorker/db/collectionRepairReason';
import { HttpError } from 'serviceWorker/helpers/httpError';
import { EntitySyncProblem, findEntitySyncProblem } from 'shared/domain/serviceData/entitySync';
import { UNPROCESSABLE_ENTITY } from 'shared/contants';
import { type RepairableService, requestCollectionDownloadAgain } from './collectionRepair';

/** The API refused the changes since the sync key (422): the stored collection is stale. */
function isSyncKeyRefused(error: unknown): boolean {
  return error instanceof HttpError && error.status === UNPROCESSABLE_ENTITY;
}

interface MakeSynchronizeEntityProps<T = unknown> {
  entityRepository: {
    clear: () => Promise<void>;
    removeBatch: (batch: any[]) => Promise<any>;
    updateBatch: (batch: T[]) => Promise<any>;
    quantity: () => Promise<number>;
  };
  entityService: {
    /** Whom the collection belongs to, as its table declares. */
    owner?: DataOwner;
    get: () => Promise<undefined | ServiceData>;
    reset: () => Promise<void>;
    add: (data: Partial<ServiceData>) => Promise<void>;
    addSync: (data: Partial<ServiceData>) => Promise<void>;
    stopDownloading: () => Promise<void>;
    clear: () => Promise<void>;
    requestDownloadAgain: () => Promise<void>;
  };
  pullEntityHandler: () => Promise<void>;
  fetchUpdatedEntities: (
    data: HeadersData,
    syncKey: string,
    abortController: AbortController
  ) => Promise<NormalizedEntityResponseData<T>> | undefined;
  finishedServiceStateFactory: (
    total: number,
    syncKey: string
  ) => Partial<ServiceData>;
  entityName: Services;
  channelContract: AnyChannelContract;
  /** The collection event, published once the sync (or the pull it starts) is stored. */
  pendingCollection: PendingPublish;
}

export function makeSynchronizeEntity<T = unknown>({
  entityService: storedService,
  entityRepository: storedEntities,
  pullEntityHandler,
  fetchUpdatedEntities,
  finishedServiceStateFactory,
  entityName,
  channelContract,
  pendingCollection,
}: MakeSynchronizeEntityProps<T>): () => Promise<boolean> {
  const prevUpdate = {
    hashString: '',
    itemsString: '',
  };
  const configRetry = createConfigRetry(`sync:${entityName}`, () => {
    void runPullInBackground({ entityName, contract: channelContract }, synchronize);
  });
  const markStep = pullStepMarker(channelContract);

  /** Whether the collection is downloaded now: the result of a pull or sync was stored. */
  const isStored = async (entityService: { get: () => Promise<ServiceData | undefined> }): Promise<boolean> =>
    Boolean((await entityService.get())?.isDownloaded);

  /**
   * Whether a downloaded collection must be downloaded again in full: it was
   * marked to (`shouldReset`), or it lost its rows (then it is marked here).
   * The changes since the sync key cannot fix either.
   */
  const needsDownloadAgain = async (
    service: ServiceData,
    entityService: RepairableService,
    countStored: () => Promise<number | undefined>
  ): Promise<boolean> => {
    if (service.shouldReset) {
      return true;
    }
    const isEmptyAfterDownload =
      findEntitySyncProblem({ serviceData: service, rowCount: (await countStored()) ?? null }) ===
      EntitySyncProblem.emptyAfterDownload;
    return (
      isEmptyAfterDownload &&
      requestCollectionDownloadAgain(entityService, entityName, CollectionRepairReason.emptyAfterDownload)
    );
  };

  /** Resolves whether the result was stored (false offline, after a failure, or when outdated). */
  async function synchronize(): Promise<boolean> {
    // Captured before the config is read: a sync started under outdated data writes nothing,
    // also when the config it reads is still the previous project's.
    const scope = captureCollectionScope(storedService, undefined, entityName);
    const entityService = {
      get: scope.guard(storedService.get),
      addSync: scope.guard(storedService.addSync),
      stopDownloading: scope.guard(storedService.stopDownloading),
      requestDownloadAgain: scope.guard(storedService.requestDownloadAgain),
    };
    const entityRepository = {
      quantity: scope.guard(storedEntities.quantity),
      removeBatch: scope.guard(storedEntities.removeBatch),
      updateBatch: scope.guard(storedEntities.updateBatch),
    };

    markStep(PullStep.readingConfig);
    const { config: setup, reason } = await resolveAuthenticatedConfig();
    if (!setup) {
      markStep(PullStep.configUnavailable);
      // Without a cooldown it goes on once the page's config is applied (it used to be dropped silently).
      logWaitsForConfig(entityName, reason, configRetry.wait(reason));
      return false;
    }

    configRetry.reset();
    if (!(await scope.isCurrent())) {
      // The config may be the previous project's: the switch starts its own downloads.
      markStep(PullStep.scopeOutdated);
      return false;
    }

    pendingCollection.request();
    const service = await entityService.get();

    const syncKey = service?.syncKey;
    // No sync key: no download stored the collection. Whether one runs is the pull's to tell.
    if (!syncKey || (await needsDownloadAgain(service, entityService, () => entityRepository.quantity()))) {
      await pullEntityHandler();
      return isStored(entityService);
    }

    const newState = {
      ...service,
      isDownloading: true,
    };
    await entityService.addSync(newState);
    const abortController = new AbortController();

    const promise = fetchUpdatedEntities(setup, syncKey, abortController);

    if (!promise) {
      // Nothing was requested (no API address or project): the state must not stay "downloading".
      markStep(PullStep.fetchSkipped);
      await entityService.stopDownloading();
      return false;
    }
    // The sync of the changes is marked like a pull: a finished one must not read as stuck at `reading-config`.
    markStep(PullStep.started);
    const abort = (): void => {
      abortController.abort();
      pendingCollection.cancel();
    };
    const unsubscribeAbort = subscribeSwLifecycleAbort(abort);

    return promise
      .then(async (data: NormalizedEntityResponseData<T>): Promise<boolean> => {
        if (!data || abortController.signal.aborted) {
          markStep(PullStep.aborted);
          return false;
        }

        if (data?.entitiesWithoutAccess.length) {
          await entityRepository.removeBatch(data.entitiesWithoutAccess);
        }
        if (data?.items.length) {
          const itemsString = JSON.stringify(data.items);
          const hashString = hashSum(data.items);

          if (!isSameAsPrev({ itemsString, hashString }, prevUpdate)) {
            prevUpdate.hashString = hashString;
            prevUpdate.itemsString = itemsString;
            await entityRepository.updateBatch(data.items);
          }
        }
        const quantity = await entityRepository.quantity();
        const finishedState = finishedServiceStateFactory(quantity ?? 0, data?.syncKey);
        await entityService.addSync(finishedState);
        markStep(PullStep.ended);
        await scope.write(async () => pendingCollection.flush());
        return scope.isCurrent();
      })
      .catch(async (e: unknown): Promise<boolean> => {
        prevUpdate.hashString = '';
        prevUpdate.itemsString = '';
        // The sync failed: the entity stays downloaded with its syncKey.
        await entityService.stopDownloading();

        if (handleAbortError(e, abortController, `syncing ${entityName}`)) {
          markStep(PullStep.aborted);
          return false;
        }
        // Handled where it was answered (cleared, downloaded again in full, logged as info): not a failure.
        if (isInvalidSyncKeyError(e)) {
          markStep(PullStep.syncKeyRefused);
          return false;
        }

        markStep(PullStep.failed);
        logEntityOperationFailure(EntityOperation.SYNCING, {
          entityName,
          projectId: setup.projectId,
          error: e,
        });
        // A refused sync key fails every sync the same way: only a full download gets out of it.
        if (
          isSyncKeyRefused(e) &&
          (await requestCollectionDownloadAgain(entityService, entityName, CollectionRepairReason.invalidSyncKey))
        ) {
          await pullEntityHandler();
          return isStored(entityService);
        }
        return false;
      })
      .finally(() => {
        unsubscribeAbort();
      });
  }

  return synchronize;
}
